// /frontend/src/pages/AdminInsights.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  useMediaQuery,
  useTheme,
  Grid,
  TextField,
  Button,
  Chip,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WorkIcon from '@mui/icons-material/Work';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import BusinessIcon from '@mui/icons-material/Business';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import FilterListIcon from '@mui/icons-material/FilterList';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const AdminInsights = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));

  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  // Filtri temporali
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    dateTo: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    if (!isMobile) {
      fetchSummary();
    }
  }, [isMobile]);

  const fetchSummary = async (customFilters = null) => {
    setLoading(true);
    setError('');
    try {
      const params = customFilters || filters;
      const queryString = new URLSearchParams(params).toString();
      const response = await axios.get(`/api/admin/insights/summary?${queryString}`);
      setData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Errore nel caricamento dei dati.');
      console.error('Errore insights:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    fetchSummary(filters);
  };

  const handleResetFilters = () => {
    const newFilters = {
      dateFrom: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      dateTo: new Date().toISOString().split('T')[0]
    };
    setFilters(newFilters);
    fetchSummary(newFilters);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const getJobTypeLabel = (type) => {
    const labels = {
      'stage': 'Stage',
      'full_time': 'Full Time',
      'part_time': 'Part Time',
      'freelance': 'Freelance'
    };
    return labels[type] || type || 'Altro';
  };

  const getPlacementPieData = (placement) => ({
    labels: ['Attivi', 'Non disponibili'],
    datasets: [
      {
        data: [placement.attivi, placement.nonDisponibili],
        backgroundColor: ['#1976d2', '#9e9e9e']
      }
    ]
  });

  const getJobTypeBarData = (items) => ({
    labels: items.map((item) => getJobTypeLabel(item.type)),
    datasets: [
      {
        label: 'Annunci',
        data: items.map((item) => item.count),
        backgroundColor: '#1976d2'
      }
    ]
  });

  const getCategoryBarData = (items) => ({
    labels: items.map((item) => item.name),
    datasets: [
      {
        label: 'Conteggio',
        data: items.map((item) => item.count),
        backgroundColor: '#9c27b0'
      }
    ]
  });

  const getProvinceBarData = (items, key) => ({
    labels: items.map((item) => item[key] || 'N/D').slice(0, 5),
    datasets: [
      {
        label: 'Conteggio',
        data: items.map((item) => item._count[key]).slice(0, 5),
        backgroundColor: '#ed6c02'
      }
    ]
  });

  const getSectorBarData = (items) => ({
    labels: items.map((item) => item.sector).slice(0, 5),
    datasets: [
      {
        label: 'Media candidature',
        data: items.map((item) => Number(item.avgApplications)).slice(0, 5),
        backgroundColor: '#2e7d32'
      }
    ]
  });

  if (isMobile) {
    return (
      <Container maxWidth="sm" sx={{ py: 4 }}>
        <Alert severity="info">
          📱 La sezione Insights è disponibile solo da tablet o desktop.
        </Alert>
      </Container>
    );
  }

  return (
    <Box sx={{ px: 1, py: 1 }}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'bold' }}>
            📊 Insights & Business Intelligence
          </Typography>
          <Button
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
            variant="outlined"
          >
            {showFilters ? 'Nascondi Filtri' : 'Mostra Filtri'}
          </Button>
        </Box>

        {/* FILTRI TEMPORALI */}
        {showFilters && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'grey.100' }}>
            <Grid container spacing={2}>
              {/* Preset Buttons */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button 
                    variant={filters.dateFrom === new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => {
                      const newFilters = {
                        dateFrom: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                        dateTo: new Date().toISOString().split('T')[0]
                      };
                      setFilters(newFilters);
                      fetchSummary(newFilters);
                    }}
                  >
                    Ultimi 30 giorni
                  </Button>
                  <Button 
                    variant={filters.dateFrom === new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => {
                      const newFilters = {
                        dateFrom: new Date(Date.now() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                        dateTo: new Date().toISOString().split('T')[0]
                      };
                      setFilters(newFilters);
                      fetchSummary(newFilters);
                    }}
                  >
                    Ultimi 90 giorni
                  </Button>
                  <Button 
                    variant={filters.dateFrom === new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => {
                      const newFilters = {
                        dateFrom: new Date(Date.now() - 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                        dateTo: new Date().toISOString().split('T')[0]
                      };
                      setFilters(newFilters);
                      fetchSummary(newFilters);
                    }}
                  >
                    Ultimo anno
                  </Button>
                </Box>
              </Grid>
              
              {/* Custom Date Range */}
              <Grid item xs={12}>
                <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 'bold' }}>Periodo Personalizzato:</Typography>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  type="date"
                  label="Da:"
                  name="dateFrom"
                  value={filters.dateFrom}
                  onChange={handleFilterChange}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  type="date"
                  label="A:"
                  name="dateTo"
                  value={filters.dateTo}
                  onChange={handleFilterChange}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={4} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Button variant="contained" size="small" onClick={handleApplyFilters}>Applica</Button>
                <Button variant="outlined" size="small" onClick={handleResetFilters}>Reset</Button>
              </Grid>
            </Grid>
          </Paper>
        )}

        {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper sx={{ width: '100%' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label="insights tabs"
            sx={{ width: '100%' }}
            variant="fullWidth"
          >
            <Tab label="Efficacia Formativa" icon={<TrendingUpIcon />} iconPosition="start" />
            <Tab label="Analisi Mercato" icon={<WorkIcon />} iconPosition="start" />
            <Tab label="Supply vs Demand" icon={<CompareArrowsIcon />} iconPosition="start" />
            <Tab label="Performance Aziende" icon={<BusinessIcon />} iconPosition="start" />
            <Tab label="Salute Community" icon={<HealthAndSafetyIcon />} iconPosition="start" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {/* TAB 1: EFFICACIA FORMATIVA */}
            {activeTab === 0 && data?.placement && (
              <Box>
                {/* KPI HIGHLIGHT */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Paper sx={{ p: 2, bgcolor: 'info.light', textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ color: 'info.dark', fontWeight: 'bold' }}>
                        {data.placement.availabilityRate}%
                      </Typography>
                      <Typography variant="caption">Disponibili (Attivi)</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Paper sx={{ p: 2, bgcolor: 'primary.light', textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ color: 'primary.dark', fontWeight: 'bold' }}>
                        {data.placement.attivi}
                      </Typography>
                      <Typography variant="caption">Alumni Attivi</Typography>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.200', textAlign: 'center' }}>
                      <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 'bold' }}>
                        {data.placement.nonDisponibili}
                      </Typography>
                      <Typography variant="caption">Non Disponibili</Typography>
                    </Paper>
                  </Grid>
                </Grid>

                {/* GRAFICO + DETTAGLI */}
                <Grid container spacing={2}>
                  {data.placement.totalAlumni > 0 && (
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, boxShadow: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                          Distribuzione Disponibilità Alumni
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                          <Box sx={{ maxWidth: 300, width: '100%' }}>
                            <Pie
                              data={getPlacementPieData(data.placement)}
                              options={{ plugins: { legend: { position: 'bottom' } } }}
                            />
                          </Box>
                        </Box>
                      </Paper>
                    </Grid>
                  )}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, boxShadow: 2 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                        Dettagli Disponibilità
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ p: 1.5, bgcolor: 'info.light', borderRadius: 1 }}>
                          <Typography variant="body2"><strong>🔍 Attivi</strong></Typography>
                          <Typography variant="h6">{data.placement.attivi}</Typography>
                          <Typography variant="caption" color="text.secondary">{data.placement.availabilityRate}% del totale</Typography>
                        </Box>
                        <Box sx={{ p: 1.5, bgcolor: 'grey.200', borderRadius: 1 }}>
                          <Typography variant="body2"><strong>🚫 Non Disponibili</strong></Typography>
                          <Typography variant="h6">{data.placement.nonDisponibili}</Typography>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>

                {/* ANALISI PER CORSO */}
                {data.placement.courseBreakdown && data.placement.courseBreakdown.length > 0 && (
                  <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                      📚 Breakdown per Corso
                    </Typography>
                    
                    {data.placement.courseBreakdown.map((course, idx) => (
                      <Paper key={idx} sx={{ p: 2, mb: 2, boxShadow: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                              {course.courseName}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {course.totalStudents} studenti • Disponibili: {course.availabilityRate}%
                            </Typography>
                          </Box>
                          <Chip 
                            label={`${course.availabilityRate}%`} 
                            color={parseFloat(course.availabilityRate) > 50 ? 'success' : 'warning'} 
                            size="small"
                          />
                        </Box>

                        <Grid container spacing={2}>
                          {/* Status Breakdown */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                              Status Studenti:
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                              <Box sx={{ p: 1, bgcolor: 'info.light', borderRadius: 1, minWidth: 100 }}>
                                <Typography variant="caption">Attivi</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                  {course.attivi}
                                </Typography>
                              </Box>
                              <Box sx={{ p: 1, bgcolor: 'grey.200', borderRadius: 1, minWidth: 100 }}>
                                <Typography variant="caption">Non Disp.</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                  {course.nonDisponibili}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>

                          {/* Top Categorie */}
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                              Top Categorie Candidature:
                            </Typography>
                            {course.topCategories.length > 0 ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                {course.topCategories.map((cat, catIdx) => (
                                  <Box key={catIdx} sx={{ display: 'flex', justifyContent: 'space-between', p: 0.5, bgcolor: 'grey.50', borderRadius: 0.5 }}>
                                    <Typography variant="body2">{cat.name}</Typography>
                                    <Chip label={cat.count} size="small" variant="outlined" />
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                Nessuna candidatura
                              </Typography>
                            )}
                          </Grid>
                        </Grid>

                        {/* Insight Intelligente */}
                        {course.insight && (
                          <Box sx={{ mt: 2, p: 1.5, bgcolor: 'info.lighter', borderLeft: '4px solid', borderColor: 'info.main', borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'info.dark' }}>
                              💡 <strong>Insight:</strong> {course.insight}
                            </Typography>
                          </Box>
                        )}
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>
            )}

            {/* TAB 2: ANALISI MERCATO */}
            {activeTab === 1 && data?.market && (
              <Box>
                {/* KPI HIGHLIGHT */}
                <Paper sx={{ p: 2, mb: 3, bgcolor: 'primary.light', textAlign: 'center' }}>
                  <Typography variant="h4" sx={{ color: 'primary.dark', fontWeight: 'bold' }}>
                    {data.market.totalJobs}
                  </Typography>
                  <Typography variant="body1">Opportunità Pubblicate</Typography>
                </Paper>

                {/* GRAFICO + BREAKDOWN */}
                {data.market.jobTypeDistribution.length > 0 && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, boxShadow: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                          Tipologie Contratti
                        </Typography>
                        <Bar
                          data={getJobTypeBarData(data.market.jobTypeDistribution)}
                          options={{
                            indexAxis: 'y',
                            plugins: { legend: { display: false } },
                            scales: { x: { beginAtZero: true } }
                          }}
                          height={data.market.jobTypeDistribution.length * 40}
                        />
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, boxShadow: 2 }}>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
                          Breakdown per Tipo
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {data.market.jobTypeDistribution.map((item, idx) => (
                            <Box key={idx} sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2"><strong>{getJobTypeLabel(item.type)}</strong></Typography>
                                <Chip label={`${item.percentage}%`} size="small" color="primary" />
                              </Box>
                              <Typography variant="caption" color="text.secondary">{item.count} annunci</Typography>
                            </Box>
                          ))}
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                )}
              </Box>
            )}

            {/* TAB 3: SUPPLY VS DEMAND */}
            {activeTab === 2 && data?.gap && (
              <Box>
                {/* SKILL GAP */}
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>🎯 Skill Gap Analysis</Typography>
                <Grid container spacing={2} sx={{ mb: 4 }}>
                  {data.gap.demand.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, boxShadow: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                          📈 Top Categorie RICHIESTE (Demand)
                        </Typography>
                        <Bar
                          data={getCategoryBarData(data.gap.demand)}
                          options={{
                            indexAxis: 'y',
                            plugins: { legend: { display: false } },
                            scales: { x: { beginAtZero: true } }
                          }}
                          height={data.gap.demand.length * 40}
                        />
                      </Paper>
                    </Grid>
                  )}
                  {data.gap.supply.length > 0 && (
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, boxShadow: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                          👥 Top Categorie OFFERTE (Supply)
                        </Typography>
                        <Bar
                          data={getCategoryBarData(data.gap.supply)}
                          options={{
                            indexAxis: 'y',
                            plugins: { legend: { display: false } },
                            scales: { x: { beginAtZero: true } }
                          }}
                          height={data.gap.supply.length * 40}
                        />
                      </Paper>
                    </Grid>
                  )}
                </Grid>

                {/* GEOGRAPHIC MISMATCH */}
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>📍 Geographic Mismatches</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, boxShadow: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        🏠 Province con Alumni
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {data.gap.geography.userProvinces.map((item, idx) => (
                          <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', p: 0.5, bgcolor: 'grey.50', borderRadius: 0.5 }}>
                            <Typography variant="body2">{item.province || 'N/D'}</Typography>
                            <Chip label={item._count.province} size="small" variant="outlined" />
                          </Box>
                        ))}
                      </Box>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, boxShadow: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                        🏢 Province con Aziende
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {data.gap.geography.businessProvinces.map((item, idx) => (
                          <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', p: 0.5, bgcolor: 'grey.50', borderRadius: 0.5 }}>
                            <Typography variant="body2">{item.province || 'N/D'}</Typography>
                            <Chip label={item._count.province} size="small" variant="outlined" />
                          </Box>
                        ))}
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}

            {/* TAB 4: PERFORMANCE AZIENDE */}
            {activeTab === 3 && data?.partners && (
              <Box>
                {/* TOP PARTNERS */}
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>🏢 Top Partner</Typography>
                <Paper sx={{ p: 2, mb: 3, boxShadow: 2 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {data.partners.topPartners.slice(0, 5).map((biz, idx) => (
                      <Box key={idx} sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid #e0e0e0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>{biz.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{biz.sector}</Typography>
                        </Box>
                        <Chip label={`${biz._count.jobs} annunci`} color="primary" />
                      </Box>
                    ))}
                  </Box>
                </Paper>

                {/* SECTOR ATTRACTIVENESS */}
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>✨ Attrattività Settori</Typography>
                {data.partners.sectorAttractiveness.length > 0 && (
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, boxShadow: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                          Media Candidature per Annuncio
                        </Typography>
                        <Bar
                          data={getSectorBarData(data.partners.sectorAttractiveness)}
                          options={{
                            indexAxis: 'y',
                            plugins: { legend: { display: false } },
                            scales: { x: { beginAtZero: true } }
                          }}
                          height={data.partners.sectorAttractiveness.length * 40}
                        />
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Paper sx={{ p: 2, boxShadow: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                          Ranking Settori
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {data.partners.sectorAttractiveness.map((sector, idx) => (
                            <Box key={idx} sx={{ p: 1.5, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2"><strong>{sector.sector}</strong></Typography>
                                <Chip label={`${sector.avgApplications} media`} size="small" color={parseFloat(sector.avgApplications) > 5 ? 'success' : 'default'} />
                              </Box>
                            </Box>
                          ))}
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                )}
              </Box>
            )}

            {/* TAB 5: SALUTE COMMUNITY */}
            {activeTab === 4 && data?.community && (
              <Box>
                <Grid container spacing={2}>
                  {/* BURNOUT RISK */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, bgcolor: 'warning.light', boxShadow: 2, minHeight: 200 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                        ⚠️ Alumni a Rischio Burnout
                      </Typography>
                      <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 'bold', mb: 1 }}>
                        {data.community.atRiskCount}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Utenti con >10 candidature ma ancora in cerca
                      </Typography>
                      {data.community.atRiskCount > 0 && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                          {data.community.atRiskUsers.slice(0, 3).map((user, idx) => (
                            <Box key={idx} sx={{ p: 1, bgcolor: 'white', borderRadius: 0.5, fontSize: '0.85rem' }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span><strong>{user.fullName}</strong></span>
                                <Chip label={user._count.applications} size="small" variant="outlined" />
                              </Box>
                            </Box>
                          ))}
                          {data.community.atRiskCount > 3 && (
                            <Typography variant="caption" sx={{ mt: 1, fontStyle: 'italic' }}>
                              +{data.community.atRiskCount - 3} altri utenti
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  {/* DORMANT USERS */}
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, bgcolor: 'error.light', boxShadow: 2, minHeight: 200 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                        💤 Utenti Dormienti
                      </Typography>
                      <Typography variant="h4" sx={{ color: 'text.primary', fontWeight: 'bold', mb: 1 }}>
                        {data.community.dormantUsers}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 2 }}>
                        Inattivi da >90 giorni
                      </Typography>
                      <Box sx={{ p: 1.5, bgcolor: 'white', borderRadius: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                          {data.community.dormantPercentage}%
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          della base alumni approvati
                        </Typography>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default AdminInsights;
